import React from 'react';

import {StrandOfDoubleStranded} from 'constants/MolbioConstants';
import {
  DEFAULT_HEIGHT,
  DEFAULT_WIDTH,
  DisplayInfo,
  PRIMER_STROKE_WIDTH,
  VisualizationSequenceSummary,
} from 'molbio/components/visualizations/SequenceVisualizationConstants';
import * as sequtil from 'molbio/SeqUtil';
import {NEAR_BLACK} from 'ui-library/Colors';
import Line from 'ui-library/svg/Line';
import {Point} from 'util/geometry/GeometryConstants';

import DisplayInfoLabel, {getDisplayInfoText} from './DisplayInfoLabel';
import {
  BASE_STROKE_COLOR,
  BASE_STROKE_WIDTH,
  FRAGMENT_STROKE_WIDTH,
  LINEAR_LEFT_POINT,
  LINEAR_RIGHT_POINT,
  TextPointInfo,
} from './SequenceVisualizationConstants';
import VisualizationContainer from './VisualizationContainer';
import {getDisplayInfos, getIndexForArrow, getLinearPointForIndex} from './VisualizationUtil';

/**
 * Returns information about where text should be placed to avoid overlaps
 */
function getLinearIndexTextPointInfos(
  start: number,
  end: number,
  length: number,
  startDisplayInfo: DisplayInfo,
  endDisplayInfo: DisplayInfo
): {start: TextPointInfo; end: TextPointInfo} {
  const startPoint = getLinearPointForIndexOnVisualization(start, length);
  const endPoint = getLinearPointForIndexOnVisualization(end, length);
  const startText = getDisplayInfoText(startDisplayInfo) || '';
  const endText = getDisplayInfoText(endDisplayInfo) || '';
  const longLength = 35;
  const shortLength = 15;

  // For ease of implementation, we always adjust the y-values. The longer text will appear
  // on top so it doesn't overlap with the dotted line for the smaller text.
  const deltaStartY = startText.length > endText.length ? longLength : shortLength;
  const deltaEndY = startText.length > endText.length ? shortLength : longLength;
  return {
    start: {point: {x: startPoint.x, y: startPoint.y - deltaStartY}, anchor: 'middle'},
    end: {point: {x: endPoint.x, y: endPoint.y - deltaEndY}, anchor: 'middle'},
  };
}

enum LinearArrowType {
  ABOVE = 'ABOVE',
  BELOW = 'BELOW',
  BOTH = 'BOTH',
}

export function LinearArrow({
  arrowCenter,
  direction,
  color,
  strokeWidth,
  arrowType = LinearArrowType.BOTH,
}: {
  arrowCenter: Point;
  direction: StrandOfDoubleStranded;
  color: string;
  strokeWidth: number;
  arrowType?: LinearArrowType;
}): JSX.Element {
  const arrowLength = 5;
  const arrowHeight = 5;
  const arrowX = arrowCenter.x - direction * arrowLength;
  return (
    <>
      {arrowType !== LinearArrowType.BELOW && (
        <Line
          startPoint={arrowCenter}
          endPoint={{x: arrowX, y: arrowCenter.y - arrowHeight}}
          strokeWidth={strokeWidth}
          stroke={color}
          strokeLinecap="round"
        />
      )}
      {arrowType !== LinearArrowType.ABOVE && (
        <Line
          startPoint={arrowCenter}
          endPoint={{x: arrowX, y: arrowCenter.y + arrowHeight}}
          strokeWidth={strokeWidth}
          stroke={color}
          strokeLinecap="round"
        />
      )}
    </>
  );
}

function getLinearPointForIndexOnVisualization(index: number, length: number, yOffset: number = 0): Point {
  return getLinearPointForIndex(index, length, LINEAR_LEFT_POINT, LINEAR_RIGHT_POINT, yOffset);
}

// Fraction of the whole sequence that the primer visualization should take up
const PRIMER_PROPORTION = 0.15;
const PRIMER_OFFSET_Y = 10;

// Assumes that startIndex is always less than endIndex and endIndex is given as a render value, not model.
export function LinearPrimersVisualization({
  fivePrimeDisplayInfo,
  threePrimeDisplayInfo,
  startIndex,
  endIndex,
  fragmentStrand,
  sequenceLength,
}: {
  fivePrimeDisplayInfo: DisplayInfo;
  threePrimeDisplayInfo: DisplayInfo;
  startIndex: number;
  endIndex: number;
  sequenceLength: number;
  fragmentStrand: StrandOfDoubleStranded;
}): JSX.Element {
  const primerLength = PRIMER_PROPORTION * sequenceLength;
  // Do not show primers if the fragment length is less than the primer visualization length
  if (endIndex - startIndex < primerLength) {
    return <></>;
  }
  const startArrowIndex = startIndex + primerLength;
  const endArrowIndex = endIndex - primerLength;
  const fivePrimeEndpointIndex = fragmentStrand === 1 ? startIndex : endIndex;
  const threePrimeEndpointIndex = fragmentStrand === 1 ? endIndex : startIndex;
  const fivePrimeArrowIndex = fragmentStrand === 1 ? startArrowIndex : endArrowIndex;
  const threePrimeArrowIndex = fragmentStrand === 1 ? endArrowIndex : startArrowIndex;

  return (
    <>
      {fivePrimeDisplayInfo.primer && (
        <>
          <Line
            startPoint={getLinearPointForIndexOnVisualization(
              fivePrimeEndpointIndex,
              sequenceLength,
              PRIMER_OFFSET_Y
            )}
            endPoint={getLinearPointForIndexOnVisualization(
              fivePrimeArrowIndex,
              sequenceLength,
              PRIMER_OFFSET_Y
            )}
            stroke={NEAR_BLACK}
            strokeWidth={PRIMER_STROKE_WIDTH}
          />
          <LinearArrow
            arrowCenter={getLinearPointForIndexOnVisualization(
              fivePrimeArrowIndex,
              sequenceLength,
              PRIMER_OFFSET_Y
            )}
            direction={fragmentStrand}
            color={NEAR_BLACK}
            strokeWidth={PRIMER_STROKE_WIDTH}
            arrowType={LinearArrowType.BELOW}
          />
        </>
      )}
      {threePrimeDisplayInfo.primer && (
        <>
          <Line
            startPoint={getLinearPointForIndexOnVisualization(
              threePrimeEndpointIndex,
              sequenceLength,
              -PRIMER_OFFSET_Y
            )}
            endPoint={getLinearPointForIndexOnVisualization(
              threePrimeArrowIndex,
              sequenceLength,
              -PRIMER_OFFSET_Y
            )}
            stroke={NEAR_BLACK}
            strokeWidth={PRIMER_STROKE_WIDTH}
          />
          <LinearArrow
            arrowCenter={getLinearPointForIndexOnVisualization(
              threePrimeArrowIndex,
              sequenceLength,
              -PRIMER_OFFSET_Y
            )}
            direction={-fragmentStrand as StrandOfDoubleStranded}
            color={NEAR_BLACK}
            strokeWidth={PRIMER_STROKE_WIDTH}
            arrowType={LinearArrowType.ABOVE}
          />
        </>
      )}
    </>
  );
}

export default function LinearFragmentVisualization({
  sequence,
  fragmentColor,
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
}: {
  sequence: VisualizationSequenceSummary;
  fragmentColor: string;
  width?: number;
  height?: number;
}): JSX.Element {
  const {start, end, strand, sequenceLength: length, fivePrimeDisplayInfo, threePrimeDisplayInfo} = sequence;
  const startPoint = getLinearPointForIndexOnVisualization(start, length);
  const endPoint = getLinearPointForIndexOnVisualization(
    end != null ? sequtil.modelEndToRender(end, length) : start,
    length
  );
  const {startDisplayInfo, endDisplayInfo} = getDisplayInfos(sequence);
  const textPointInfos = getLinearIndexTextPointInfos(
    start,
    end ?? start,
    length,
    startDisplayInfo,
    endDisplayInfo
  );
  const arrowIndex = getIndexForArrow(start, end, strand, length);
  const arrowCenter = getLinearPointForIndexOnVisualization(arrowIndex, length);
  return (
    <VisualizationContainer width={width} height={height}>
      <Line // The whole "base" sequence
        startPoint={LINEAR_LEFT_POINT}
        endPoint={LINEAR_RIGHT_POINT}
        stroke={BASE_STROKE_COLOR}
        strokeWidth={BASE_STROKE_WIDTH}
      />
      <DisplayInfoLabel // Text label (enzyme/bases) for fragment start
        sequencePoint={startPoint}
        textPointInfo={textPointInfos.start}
        displayInfo={startDisplayInfo}
      />
      <DisplayInfoLabel // Text label (enzyme/bases) for fragment end
        sequencePoint={endPoint}
        textPointInfo={textPointInfos.end}
        displayInfo={endDisplayInfo}
      />
      <LinearArrow // Arrow to show directionality
        arrowCenter={arrowCenter}
        direction={strand}
        color={fragmentColor}
        strokeWidth={FRAGMENT_STROKE_WIDTH}
      />
      <Line // Line showing which part of the sequence is actually selected
        startPoint={startPoint}
        endPoint={endPoint}
        stroke={fragmentColor}
        strokeWidth={FRAGMENT_STROKE_WIDTH}
      />
      {end != null && (
        <LinearPrimersVisualization
          fivePrimeDisplayInfo={fivePrimeDisplayInfo}
          threePrimeDisplayInfo={threePrimeDisplayInfo}
          startIndex={start}
          endIndex={sequtil.modelEndToRender(end, length)}
          sequenceLength={length}
          fragmentStrand={strand}
        />
      )}
    </VisualizationContainer>
  );
}
